import React from "react";
import { motion } from "framer-motion";
import { Eye, PencilLine, ClipboardCheck, MessageSquare } from "lucide-react";

const evaluationSteps = [
    {
        icon: <Eye size={22} />,
        title: "Detailed Review",
        description:
            "Each answer is read thoroughly by experienced mentors with attention to structure, content, and relevance.",
    },
    {
        icon: <PencilLine size={22} />,
        title: "Line-by-Line Feedback",
        description:
            "Mentors annotate your answer with constructive comments and highlight areas of improvement.",
    },
    {
        icon: <ClipboardCheck size={22} />,
        title: "Scoring & Rubric",
        description:
            "Every answer is scored based on UPSC parameters: content, language, coherence, presentation & relevance.",
    },
    {
        icon: <MessageSquare size={22} />,
        title: "Personal Comments",
        description:
            "A concluding comment is shared summarizing the answer quality and actionable next steps.",
    },
];

const MentorEvaluation = () => {
    // Professional animation variants matching the established theme
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.05 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 w-full overflow-hidden border-t border-gray-200">
            <div className="max-w-7xl mx-auto relative z-10">
                {/* Standardized Editorial Header (Centered for Pipeline Layout) */}
                <div className="text-center mb-20 max-w-2xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-black text-neutral-900 mb-4">
                            How Mentor{" "}
                            <span className="text-yellow-500">Evaluation</span>{" "}
                            Works.
                        </h2>
                        <p className="text-gray-500 text-lg font-medium leading-relaxed">
                            Experience a highly structured, transparent, and
                            data-driven evaluation process designed to maximize
                            your mains score.
                        </p>
                    </motion.div>
                </div>

                {/* Process Pipeline Grid */}
                <div className="relative">
                    {/* Desktop Connector Line */}
                    <div className="hidden lg:block absolute top-1/2 left-0 w-full h-[2px] bg-gray-200 border-t-2 border-dashed border-gray-300 -translate-y-[2rem] z-0"></div>

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 relative z-10"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        {evaluationSteps.map((step, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                className="group relative bg-white p-8 rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col h-full"
                            >
                                {/* Background Watermark Number */}
                                <div className="absolute top-2 right-4 text-8xl font-black text-yellow-400 opacity-60 group-hover:text-yellow-500 group-hover:-translate-y-2 transition-all duration-500 select-none z-0">
                                    {index + 1}
                                </div>

                                {/* Step Content */}
                                <div className="relative z-10 flex flex-col h-full">
                                    {/* Icon Container (Matches TestPackage & Features) */}
                                    <div className="w-12 h-12 bg-gray-50 group-hover:bg-yellow-400 text-neutral-900 rounded-lg flex items-center justify-center transition-colors duration-300 border border-gray-100 group-hover:border-yellow-400 mb-6 shadow-sm">
                                        {step.icon}
                                    </div>

                                    <h3 className="text-xl font-bold text-neutral-900 mb-3 group-hover:text-red-700 transition-colors duration-300 leading-snug">
                                        {step.title}
                                    </h3>

                                    <p className="text-gray-500 text-sm leading-relaxed font-medium flex-grow">
                                        {step.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default MentorEvaluation;
